<script setup lang="ts">
/**
 * La page « Surveillance » (écran 1 du dossier), en lecture.
 *
 * Ce que l'utilisateur doit vivre : il lit la liste des surveillances de son
 * entreprise, nommées par leur type, avec l'équipement, le lieu, l'état en
 * toutes lettres et l'alarme en cours, celles à traiter d'abord. Un service
 * tombé ou un refus se dit à la place de la liste, jamais par une bannière,
 * jamais par un 0. Hors de l'offre Energy Manager, `CadreModule` rend l'écran
 * hors offre et rien n'est lu.
 */
// ================================
// IMPORTS
// ================================
import { computed, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useAuthStore } from "@core/stores/auth.store"
import { useSurveillancesStore } from "@modules/alarmes-v2/stores/surveillances.store"
import { useTypesStore } from "@modules/alarmes-v2/stores/types.store"
import { cleDuRefus } from "@modules/alarmes-v2/utils/refus"
import { useAcces } from "@modules/alarmes-v2/acces/useAcces"
import { CHEMIN_CATALOGUE } from "@modules/alarmes-v2/utils/menu"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

/**
 * La session connectée.
 */
const authStore = useAuthStore()

/**
 * Les surveillances et leurs alarmes ouvertes.
 */
const surveillancesStore = useSurveillancesStore()

/**
 * Le catalogue, pour le nom des types.
 */
const typesStore = useTypesStore()

/**
 * Le lien vers le catalogue, seulement si la matrice ouvre « Voir le catalogue ».
 */
const { acces } = useAcces()
const getVoitLeCatalogue = computed(() => acces("catalogue_voir").ok)

/**
 * Ce que la page montre à la place de la liste, ou `liste`.
 */
const getVue = computed((): "chargement" | "erreur" | "vide" | "liste" => {
    if (surveillancesStore.statut === "erreur") return "erreur"
    if (surveillancesStore.surveillances.length > 0) return "liste"
    if (
        surveillancesStore.statut === "pret" &&
        !surveillancesStore.suiteDisponible
    )
        return "vide"
    return surveillancesStore.statut === "pret" ? "liste" : "chargement"
})

/**
 * La phrase du refus ou de l'indisponibilité, à la place de la liste.
 */
const getPhraseErreur = computed((): string => {
    const erreur = surveillancesStore.erreur
    return t(erreur ? cleDuRefus(erreur) : "alarmesV2.refus.indisponible")
})

/**
 * Les alarmes ouvertes, par surveillance.
 */
const getAlarmes = computed(() =>
    Object.fromEntries(
        surveillancesStore.surveillances
            .map((s) => [s._id, surveillancesStore.alarmeDe(s._id)] as const)
            .filter(([, a]) => a !== null)
    )
)

/**
 * Lit la liste et le catalogue de l'entreprise courante, si elle a l'offre.
 * La liste est relue à chaque entrée sur la page : un état d'alarme lu plus tôt
 * n'est jamais resservi. Le catalogue, lui, ne change pas en cours de session.
 */
function lire(): void {
    if (!authStore.hasOfferEnergyManager) return
    void surveillancesStore.charger({ relire: true })
    void typesStore.charger()
}

// Relue à chaque entrée sur la page, et quand l'entreprise ou l'offre change.
watch(() => [authStore.companyId, authStore.hasOfferEnergyManager], lire, {
    immediate: true,
})

/**
 * Relit les alarmes de la liste après un acquittement (lot F6), sans la
 * vider : la liste et « Afficher la suite » restent. Vrai si elle a abouti.
 */
async function relireLaListe(): Promise<boolean> {
    if (!authStore.hasOfferEnergyManager) return false
    return surveillancesStore.relireLesAlarmes()
}

/**
 * Relit la liste après un échec.
 */
function onRelire(): void {
    surveillancesStore.reinitialiser()
    lire()
}

/**
 * Reprend la lecture après le plafond de pages vides.
 */
function onAfficherLaSuite(): void {
    void surveillancesStore.afficherLaSuite()
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <CadreModule>
        <div class="mx-auto flex max-w-7xl flex-col gap-4 p-4 md:p-6">
            <!-- ================================ -->
            <!-- EN-TÊTE -->
            <!-- ================================ -->
            <header>
                <h1 class="text-2xl font-semibold text-surface-900">
                    {{ t("alarmesV2.titre") }}
                </h1>
                <p class="mt-1 text-sm text-surface-700">
                    {{ t("alarmesV2.liste.intro") }}
                </p>
                <div class="flex flex-wrap gap-x-4">
                    <!-- Lot F4 : le catalogue des types d'alarme. -->
                    <RouterLink
                        v-if="getVoitLeCatalogue"
                        :to="CHEMIN_CATALOGUE"
                        class="mt-2 inline-block text-sm font-medium text-primary-700 underline"
                        data-test="vers-catalogue"
                        >{{ t("alarmesV2.catalogue.lien") }}</RouterLink
                    >
                    <!-- Lot F5 : l'option Avancé et les SMS. [PROPOSITION sur l'emplacement] -->
                    <RouterLink
                        :to="{ name: 'PageSurveillanceOption' }"
                        class="mt-2 inline-block text-sm font-medium text-primary-700 underline"
                        data-test="lien-option-avance"
                        >{{ t("alarmesV2.option.lien") }}</RouterLink
                    >
                </div>
            </header>

            <!-- ================================ -->
            <!-- CONTENU PRINCIPAL -->
            <!-- ================================ -->
            <section class="rounded-lg border border-surface-200 bg-surface-0">
                <p
                    v-if="getVue === 'chargement'"
                    class="px-4 py-6 text-sm text-surface-600"
                >
                    {{ t("alarmesV2.liste.chargement") }}
                </p>
                <div
                    v-else-if="getVue === 'erreur'"
                    class="flex flex-wrap items-center gap-3 px-4 py-6"
                    data-test="erreur"
                >
                    <p class="text-sm text-surface-800">
                        {{ getPhraseErreur }}
                    </p>
                    <button
                        type="button"
                        class="text-sm font-medium text-primary-700 underline"
                        @click="onRelire"
                    >
                        {{ t("alarmesV2.refus.relire") }}
                    </button>
                </div>
                <p
                    v-else-if="getVue === 'vide'"
                    class="px-4 py-6 text-sm text-surface-600"
                    data-test="vide"
                >
                    {{ t("alarmesV2.liste.vide") }}
                </p>
                <ListeSurveillances
                    v-else
                    :surveillances="surveillancesStore.surveillances"
                    :alarmes="getAlarmes"
                    :personnes="surveillancesStore.personnes"
                    :relire="relireLaListe"
                />

                <!-- ================================ -->
                <!-- SUITE DE LA LECTURE -->
                <!-- ================================ -->
                <div
                    v-if="surveillancesStore.suiteDisponible"
                    class="flex flex-wrap items-center gap-3 border-t border-surface-200 px-4 py-3"
                >
                    <span class="text-xs text-surface-600">{{
                        t("alarmesV2.liste.suiteAide")
                    }}</span>
                    <button
                        type="button"
                        class="text-sm font-medium text-primary-700 underline"
                        data-test="suite"
                        @click="onAfficherLaSuite"
                    >
                        {{ t("alarmesV2.liste.suite") }}
                    </button>
                </div>
            </section>
        </div>
    </CadreModule>
</template>
